// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { cn } from "@jornada/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  class?: string;
  /**
   * Optional richer inline SVG illustration shown instead of the icon plate.
   * Decorative (aria-hidden inside); inherits `text-accent` so it stays
   * theme-safe. Falls back to the icon-on-plate when omitted.
   */
  illustration?: Component<ComponentProps<"svg">>;
  /** Optional call-to-action (e.g. a Button) rendered under the description.*/
  action?: JSX.Element;
  /**
   * When provided, the illustrative icon plate becomes an interactive target
   * that triggers the same action as the primary CTA (open a dialog, navigate,
   * or focus an input) so the empty state never feels like a dead end. Tapping
   * the icon is a discoverable shortcut to the CTA — leave it undefined for
   * purely informational empty states (the plate stays decorative). Only the
   * icon-plate fallback becomes interactive; the richer `illustration` variant
   * stays decorative.
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive icon (used when `onIconActivate` is
   * set). Should describe the action, e.g. "Add your first inspection". Falls
   * back to the `title` when omitted.
   */
  iconActionLabel?: string;
}

export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <Show
      when={props.illustration}
      fallback={
        <div class="animate-scale-in">
          {/* When an action is wired, the icon plate becomes a real button so
              tap/click/keyboard (Enter/Space, native to <button>) triggers the
              CTA. Otherwise it stays a decorative div. Focus ring is visible;
              the float animation is already suppressed under the global
              prefers-reduced-motion guard. */}
          <Show
            when={props.onIconActivate}
            fallback={
              // Jornada plate: accent-tinted rounded square with a soft dashed frame.
              <div class="mb-6 grid place-items-center rounded-2xl bg-accent/8 p-6 ring-1 ring-inset ring-accent/20 outline outline-1 outline-offset-4 outline-dashed outline-border/50 animate-float">
                <props.icon class="h-12 w-12 text-accent/70" />
              </div>
            }
          >
            {(activate) => (
              <button
                type="button"
                aria-label={props.iconActionLabel ?? props.title}
                onClick={() => {
                  activate()();
                }}
                class="group mb-6 grid place-items-center rounded-2xl bg-accent/8 p-6 ring-1 ring-inset ring-accent/20 outline outline-1 outline-offset-4 outline-dashed outline-border/50 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:bg-accent/15 hover:ring-accent/40 hover:shadow-lg hover:shadow-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
              >
                <props.icon class="h-12 w-12 text-accent/70 transition-colors group-hover:text-accent" />
              </button>
            )}
          </Show>
        </div>
      }
    >
      {(Illustration) => (
        <div class="animate-scale-in">
          {/* Same interactive affordance for the richer illustration: when an
              action is wired the artwork becomes a button (tap/click/keyboard);
              otherwise it stays decorative. */}
          <Show
            when={props.onIconActivate}
            fallback={
              <div class="mb-6 text-accent/70">
                <Dynamic component={Illustration()} class="h-36 w-36" />
              </div>
            }
          >
            {(activate) => (
              <button
                type="button"
                aria-label={props.iconActionLabel ?? props.title}
                onClick={() => {
                  activate()();
                }}
                class="group mb-6 rounded-2xl p-2 text-accent/70 transition-[transform,filter] duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
              >
                <Dynamic component={Illustration()} class="h-36 w-36" />
              </button>
            )}
          </Show>
        </div>
      )}
    </Show>
    <h3 class="text-lg font-bold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
        {props.description}
      </p>
    </Show>
    <Show when={props.action}>
      <div class="mt-6">{props.action}</div>
    </Show>
  </div>
);
